import { Button, useToast } from '@bbpos/ui';
import { formatDocAmount } from '@/utils/purchase-currency';
import { usePurchaseReturns, usePostPurchaseReturn, useCancelPurchaseReturn } from './use-purchase-returns-query';
import type { PurchaseReturnStatus, DebitNoteStatus } from './types';

const RETURN_TONE: Record<PurchaseReturnStatus, string> = {
  DRAFT: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300',
  POSTED: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300',
  CANCELLED: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
};

export const DEBIT_NOTE_TONE: Record<DebitNoteStatus, string> = {
  OPEN: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  APPLIED: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300',
  REFUNDED: 'bg-brand-100 text-brand-800 dark:bg-brand-900/30 dark:text-brand-300',
  CANCELLED: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
};

export const DEBIT_NOTE_LABEL: Record<DebitNoteStatus, string> = {
  OPEN: 'Credit open', APPLIED: 'Set off', REFUNDED: 'Refunded', CANCELLED: 'Cancelled',
};

function fmtDate(v: string | null | undefined) {
  return v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

/** Returns to supplier raised on a PO, with their debit notes (QA PU3-005). Hidden while there are none. */
export function PoReturnsCard({ poId, currency, isOwner, isManager }: { poId: string; currency: string; isOwner: boolean; isManager: boolean }) {
  const { toast } = useToast();
  const { data: returns } = usePurchaseReturns({ purchase_order_id: poId });
  const postMutation = usePostPurchaseReturn();
  const cancelMutation = useCancelPurchaseReturn();
  if (!returns || returns.length === 0) return null;

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 border border-neutral-100 dark:border-neutral-700 animate-fade-in">
      <div className="px-5 py-3 border-b border-neutral-100 dark:border-neutral-700 bg-gradient-to-r from-violet-50 to-pink-50 dark:from-violet-900/20 dark:to-pink-900/20 rounded-t-xl">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Returns to supplier</h3>
      </div>
      <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
        {returns.map((r) => (
          <li key={r.id} className="px-5 py-3 text-sm animate-slide-up even:bg-neutral-50 dark:even:bg-neutral-900/40">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold text-neutral-900 dark:text-white">{r.return_number}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RETURN_TONE[r.status]}`}>{r.status === 'POSTED' ? 'Sent back' : r.status === 'DRAFT' ? 'Draft' : 'Cancelled'}</span>
                <span className="text-xs text-neutral-500">from {r.grn_number} · {fmtDate(r.posted_at ?? r.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                {r.debit_note_number && r.debit_note_status && (
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-xs text-neutral-700 dark:text-neutral-300">{r.debit_note_number}</span>
                    <span className="tabular-nums font-semibold text-neutral-900 dark:text-white">{formatDocAmount(r.debit_note_total ?? 0, r.currency_code ?? currency)}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${DEBIT_NOTE_TONE[r.debit_note_status]}`}>{DEBIT_NOTE_LABEL[r.debit_note_status]}</span>
                  </span>
                )}
                {r.status === 'DRAFT' && isOwner && (
                  <Button size="sm" variant="primary" loading={postMutation.isPending && postMutation.variables === r.id}
                    onClick={() => postMutation.mutate(r.id, {
                      onSuccess: (p) => toast({ title: `${p.return_number} posted${p.debit_note ? ` — debit note ${p.debit_note.number}` : ''}`, variant: 'success' }),
                      onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
                    })}>
                    Post return
                  </Button>
                )}
                {r.status === 'DRAFT' && isManager && (
                  <Button size="sm" variant="ghost" loading={cancelMutation.isPending && cancelMutation.variables === r.id}
                    onClick={() => cancelMutation.mutate(r.id, {
                      onSuccess: () => toast({ title: `${r.return_number} cancelled`, variant: 'success' }),
                      onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
                    })}>
                    Cancel draft
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
              {r.items.map((i) => `${i.item_name} × ${i.quantity}`).join(' · ')} — {r.reason}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
